import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import {
  GalleryImage,
  CarouselImage,
  AltButton,
  Button,
  UploadForm,
  Modal,
  ProjectPreview
} from 'components';
import { LocalContext, FirebaseContext } from 'context';
import { fullGridContainer, flexContainer, gridContainer, fullWidth } from 'styles';
import LeftArrow from 'assets/svgs/left-arrow.svg';
import RightArrow from 'assets/svgs/right-arrow.svg';
import { wrap } from '@popmotion/popcorn';
import { motion, AnimatePresence } from 'framer-motion';

const GalleryContainer = () => {
  const [galleryView, setGalleryView] = useState(false);
  const { theme, mobile, galleryRef, registerRef, scrollTo } = useContext(LocalContext);
  const { user, firebase } = useContext(FirebaseContext);

  // In Firebase the extension was 320x320
  const data = useStaticQuery(
    graphql`
      query {
        allProject {
          edges {
            node {
              id
              title
              author
              summary
              timestamp
              disableModal
              localPDF {
                publicURL
              }
              localPreviewImg {
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED
                    formats: [AUTO, WEBP]
                    width: 440
                    height: 623
                    placeholder: TRACED_SVG
                  )
                }
              }
            }
          }
        }
      }
    `
  );

  const projects = data.allProject.edges
    .filter(({ node }) => (firebase?.auth?.currentUser ? !node.disableModal : node.disableModal))
    .map((project, index) => ({
      node: {
        ...project.node,
        index
      }
    }));

  const [[page], setPage] = useState([0, 0]);

  const [[galleryPage], setGalleryPage] = useState([0, 0]);

  const paginate = (dir) => {
    setPage([page + dir, dir]);
  };

  const galleryNext = () => {
    if (galleryPage + 9 <= projects.length) setGalleryPage([galleryPage + 10, 1]);
  };

  const galleryPrev = () => {
    if (galleryPage >= 9) setGalleryPage([galleryPage - 10, -1]);
  };

  const index = wrap(0, projects.length, page);
  const leftIndex = wrap(0, projects.length, page - 1);
  const rightIndex = wrap(0, projects.length, page + 1);

  return (
    <Container theme={theme} ref={galleryRef}>
      <H3>Poster Gallery</H3>
      <P>
        Our virtual poster gallery is the perfect opportunity for you to share your research efforts
        and connect with the wider community.
      </P>
      {(galleryView || mobile) && (
        <>
          <AnimatePresence>
            <GalleryView
              initial={{ opacity: 0, zIndex: 0 }}
              animate={{ opacity: 1, zIndex: 1 }}
              exit={{ opacity: 0, zIndex: 0 }}>
              {projects.length > 0 &&
                projects
                  .map(({ node }) => {
                    return (
                      <GalleryImage
                        id={node.index}
                        image={getImage(node.localPreviewImg.childImageSharp)}
                        alt={node.title}
                        disableModal={node.disableModal}
                      />
                    );
                  })
                  .slice(galleryPage, galleryPage + 10)}
            </GalleryView>
          </AnimatePresence>
          {user && (galleryView || mobile) && (
            <GalleryViewButtons theme={theme}>
              <button type="button" onClick={() => galleryPrev()}>
                &larr; Prev
              </button>
              <button type="button" onClick={() => galleryNext()}>
                Next &rarr;
              </button>
            </GalleryViewButtons>
          )}
        </>
      )}
      {!galleryView && !mobile && (
        <CarouselView>
          {projects.length > 0 && (
            <>
              <Left theme={theme} onClick={() => paginate(-1)}>
                <LeftArrow />
              </Left>
              <CarouselImage
                id={projects[leftIndex].node.index}
                image={getImage(projects[leftIndex].node.localPreviewImg.childImageSharp)}
                alt={projects[leftIndex].node.title}
                disableModal={projects[leftIndex].node.disableModal}
              />
              <CarouselImage
                id={projects[index].node.index}
                image={getImage(projects[index].node.localPreviewImg.childImageSharp)}
                alt={projects[index].node.title}
                disableModal={projects[index].node.disableModal}
              />
              <CarouselImage
                id={projects[rightIndex].node.index}
                image={getImage(projects[rightIndex].node.localPreviewImg.childImageSharp)}
                alt={projects[rightIndex].node.title}
                disableModal={projects[rightIndex].node.disableModal}
              />
              <Right theme={theme} onClick={() => paginate(1)}>
                <RightArrow />
              </Right>
            </>
          )}
        </CarouselView>
      )}
      {user ? (
        <>
          <P style={{ marginBottom: '0rem' }}>Click on posters to see the comments</P>
          <P>
            Please contact{' '}
            <a href="mailto:research@fightingblindness.ie">research@fightingblindness.ie</a> with
            any queries.{' '}
          </P>
        </>
      ) : (
        <>
          <RegText>
            Register here for a chance to have your poster seen and commented on at&nbsp;
            <strong>Retina 2021</strong>.
          </RegText>
          <P>
            {' '}
            <Button
              whileTap={{
                scale: 0.9
              }}
              width="12rem"
              onClick={() => {
                scrollTo(registerRef);
              }}
              style={{ margin: '1rem auto' }}>
              Register Here
            </Button>
          </P>
        </>
      )}
      <GalleryFooter>
        {!mobile && (
          <Toggle view={galleryView} theme={theme}>
            <AltButton onClick={() => setGalleryView(false)}>Carousel View</AltButton>
            <AltButton onClick={() => setGalleryView(true)}>Gallery View</AltButton>
          </Toggle>
        )}
        {user && (
          <TriggerList theme={theme}>
            <Modal
              trigger={
                <Button
                  whileTap={{
                    scale: 0.9
                  }}
                  width="7.5rem">
                  Project List
                </Button>
              }
              modalContent={
                <ProjectList>
                  {projects.map(({ node }) => (
                    <Modal
                      disableModal={node.disableModal}
                      trigger={
                        <p>
                          {node.title}
                          <br />
                          <span>{node.author}</span>
                        </p>
                      }
                      modalContent={<ProjectPreview startPoint={node.index} />}
                    />
                  ))}
                </ProjectList>
              }
            />
          </TriggerList>
        )}
        {user && (
          <Upload>
            <Modal
              trigger={
                <Button
                  whileTap={{
                    scale: 0.9
                  }}
                  width="7.5rem">
                  Upload
                </Button>
              }
              modalContent={<UploadForm />}
            />
          </Upload>
        )}
      </GalleryFooter>
    </Container>
  );
};

const Container = styled.div`
  ${fullGridContainer}
  background: ${({ theme }) => theme.bgLight};
  padding: 3.75rem 1.25rem;
  p,
  h3 {
    color: ${({ theme }) => theme.textColor};
  }
  @media (min-width: 768px) {
    padding: 6rem 1.25rem;
  }
  @media (min-width: 1600px) {
    padding: 10rem 0;
  }
`;

const GalleryView = styled(motion.div)`
  ${flexContainer}
  justify-content:center;
  align-items: center;
  flex-wrap: wrap;
  > div {
    margin: 0.5rem;
    width: 120px;
    @media (min-width: 768px) {
      margin: 1rem;
      width: 220px;
    }
  }
`;

const Left = styled.button`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
  justify-self: end;
  background-color: ${({ theme }) => theme.fg};
  padding-left: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;

  :hover {
    transform: scale(1.05);
    opacity: 1;
  }
`;

const Right = styled.button`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
  justify-self: start;
  background-color: ${({ theme }) => theme.fg};
  padding-right: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;
  :hover {
    transform: scale(1.05);
    opacity: 1;
  }
`;

const CarouselView = styled.div`
  ${fullGridContainer}
  height:auto;
  margin-bottom: 2rem;
  align-items: center;
  position: relative;
  @media (min-width: 1150px) {
    margin-bottom: 2rem;
    margin-bottom: 4rem;
    margin: 0 auto;
    max-width: 1440px;
    width: 100%;
  }
`;

const Toggle = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-transform: uppercase;
  grid-column: 1/7;
  justify-content: center;
  button {
    text-align: center;
    justify-content: center;
    width: auto;
    &:first-child {
      color: ${({ view, theme }) => (view === true ? '#c4c4c4' : view === false ? theme.fg : null)};
    }
    &:last-child {
      color: ${({ view, theme }) => (view === true ? theme.fg : view === false ? '#c4c4c4' : null)};
    }
    &:first-child:after {
      content: '/';
      margin-left: 0.625rem;
      margin-right: 0.625rem;
    }
  }

  @media (min-width: 1150px) {
    grid-column: 4/8;
  }
`;

const GalleryFooter = styled.div`
  ${gridContainer}
  padding: 0px 1.25rem;
  align-items: center;
`;
const Upload = styled.div`
  grid-column: 1/7;
  display: flex;
  justify-content: center;
  @media (min-width: 1150px) {
    grid-column: 9/11;
    justify-content: flex-end;
  }
`;

const H3 = styled.h3`
  font-style: normal;
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1.75rem;
  text-align: center;
  margin-bottom: 2rem;
  ${fullWidth}
  @media (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

const P = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 2rem;
  text-align: center;
  margin-bottom: 2rem;
  :nth-child(3) {
    margin-bottom: 4rem;
  }
  ${fullWidth}
  @media (min-width: 1150px) {
    font-size: 1.5rem;
  }
  @media (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

const RegText = styled(P)`
  max-width: 750px;
  margin: 0 auto;
`;

const GalleryViewButtons = styled.div`
  ${fullWidth}
  width: auto;
  display: flex;
  margin: 0 auto;
  button {
    margin: 2rem 1rem;
    background: none;
    border: none;
    outline: none;
    color: ${({ theme }) => theme.textColor};
    cursor: pointer;
    font-size: 1.25rem;
  }
`;

const TriggerList = styled.div`
  grid-column: 1/7;
  display: flex;
  justify-content: center;
  button {
    width: 12rem;
    background: ${({ theme }) => theme.textColor};
    margin-bottom: 3rem;
    @media (min-width: 1150px) {
      margin-bottom: 0;
    }
    :hover {
      opacity: 1;
    }
  }
  @media (min-width: 1150px) {
    grid-column: 3/9;
  }
`;

const ProjectList = styled.div`
  max-width: 100%;
  width: 800px;
  padding: 2rem;
  padding-top: 5rem;
  margin-bottom: 3rem;
  max-width: 100%;
  background: white;
  box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.25);
  p {
    width: 100%;
    font-size: 1rem;
    line-height: 1.25rem;
    margin-bottom: 1em;
    text-align: left;
    font-weight: 700;
    span {
      font-weight: 400;
    }
  }
`;

export default GalleryContainer;
